import _ from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TinderCard from 'react-tinder-card';
import heartIcon from '../../../assets/icons/heart.png';
import crossIcon from '../../../assets/icons/cross.png';
import { useDating } from '../../../hooks/use-dating/useDating';
import { IDatingProfileModel } from '../../../models/dating.model';
import { CircleUser, UserRoundCheck } from 'lucide-react';
import { MdOutlineInterests, MdOutlineNaturePeople } from 'react-icons/md';
import {
  FaBriefcase,
  FaCalendarDay,
  FaCity,
  FaGraduationCap,
  FaUserCircle,
} from 'react-icons/fa';
// import Skeleton from 'react-loading-skeleton';
// import 'react-loading-skeleton/dist/skeleton.css';
import DialogComponent from '../../../components/dialog/dialog.component';
import { datingService } from '../../../services/dating.service';
import Profile, { gender } from './profile';
import Albums from './albums';
import toast from 'react-hot-toast';
import Slider from 'react-slick';

interface TinderCardWithSwipe {
  swipe: (dir?: 'left' | 'right') => Promise<void>;
  restoreCard: () => Promise<void>;
}

const RenderInfoField = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
}) => {
  return (
    <div className="flex gap-2 items-center truncate">
      <div>{icon}</div>
      <span className="text-nowrap">{label}:</span>
      <span className="font-semibold text-black dark:text-white max-h-52  truncate">
        {value || 'Chưa có'}
      </span>
    </div>
  );
};

const Connection = ({ changeTab }: { changeTab?: (tab: string) => void }) => {
  const {
    userDatingProfiles,
    getUserDatingProfiles,
    isLoading,
    datingProfile: myProfile,
  } = useDating({});
  // Đợi tải xong hồ sơ để phân biệt "đang tải" với "chưa hoàn thiện" (tránh nháy).
  const [profileLoaded, setProfileLoaded] = useState(false);
  const isProfileComplete = !!myProfile?.isCompleted;
  const [visible, setVisible] = useState(false);
  const [visible2, setVisible2] = useState(false);
  const [profile, setProfile] = useState<IDatingProfileModel>();
  const [currentProfileId, setCurrentProfileId] = useState('');
  const [people, setPeople] = useState<IDatingProfileModel[]>([]);
  const [currentIndex, setCurrentIndex] = useState(people.length - 1);
  const [lastDirection, setLastDirection] = useState('');
  const [isOutOfFrame, setIsOutOfFrame] = useState(false);

  const isOutOfPeople = useMemo(() => {
    return (
      people.length === 0 &&
      currentIndex === people.length - 1 &&
      !!currentProfileId
    );
  }, [people, currentIndex, currentProfileId]);

  useEffect(() => {
    getUserDatingProfiles();
  }, []);

  // Đánh dấu đã tải hồ sơ: khi myProfile có giá trị, hoặc sau 500ms (trường hợp chưa có hồ sơ).
  useEffect(() => {
    if (myProfile !== undefined) {
      setProfileLoaded(true);
      return;
    }
    const t = setTimeout(() => setProfileLoaded(true), 500);
    return () => clearTimeout(t);
  }, [myProfile]);

  useEffect(() => {
    if (_.isEmpty(userDatingProfiles)) return;

    setPeople(_.get(userDatingProfiles, 'data', []));
    setCurrentIndex(_.get(userDatingProfiles, 'data', []).length - 1);
  }, [userDatingProfiles]);

  const sendConnection = useCallback(async () => {
    // profileId: string, direction: 'left' | 'right'
    if (!myProfile) return;
    if (!isOutOfFrame) return;
    if (lastDirection === 'left') {
      toast.error('Không thích', { position: 'top-center' });
    }
    if (lastDirection === 'right') {
      await datingService.sendMatchRequest({
        receivedProfileId: currentProfileId,
        requestProfileId: myProfile?.profileId,
      });
      toast.success('Đã gửi kết nối', { position: 'top-center' });
    }
  }, [lastDirection, isOutOfFrame, currentProfileId]);

  useEffect(() => {
    sendConnection();
  }, [sendConnection]);

  const canGoBack = currentIndex < people.length - 1;

  const currentIndexRef = useRef(currentIndex);

  const canSwipe = currentIndex >= 0;

  const childRefs = useMemo(
    () =>
      Array(people.length)
        .fill(0)
        .map(() => React.createRef<TinderCardWithSwipe>()),
    [people]
  );

  const swiped = (direction: string, nameToDelete: string, index: number) => {
    setLastDirection(direction);
    setIsOutOfFrame(false);
    updateCurrentIndex(index - 1);
  };

  const updateCurrentIndex = (val: number) => {
    setCurrentIndex(val);
    currentIndexRef.current = val;
  };
  const outOfFrame = (profileId: string, idx: number) => {
    setPeople((prev) => prev.filter((item) => item.profileId !== profileId));
    setIsOutOfFrame(true);
    setCurrentProfileId(profileId);
    if (currentIndexRef.current >= idx) {
      childRefs[idx].current?.restoreCard();
    }
  };

  const swipe = async (dir: string) => {
    // Chốt chặn: chưa hoàn thiện hồ sơ thì không cho quẹt.
    if (!isProfileComplete) {
      toast.error('Hãy hoàn thiện hồ sơ hẹn hò trước khi quẹt', {
        position: 'top-center',
      });
      changeTab?.('profile');
      return;
    }
    if (canSwipe && currentIndex < people.length) {
      setIsOutOfFrame(false);
      await childRefs[currentIndex].current?.swipe(dir as 'left' | 'right');
    }
  };

  const goBack = async () => {
    if (!canGoBack) return;
    const newIndex = currentIndex + 1;
    updateCurrentIndex(newIndex);
    await childRefs[newIndex].current?.restoreCard();
  };

  const openViewProfile = (profile: IDatingProfileModel) => {
    setProfile(profile);
    setVisible(true);
  };

  const sliderRef = useRef<Slider>(null);
  const settings = {
    dots: false,
    infinite: profile && profile?.albumUrls?.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  // Đang tải hồ sơ.
  if (!profileLoaded) {
    return (
      <div className="mt-8 flex min-h-[400px] items-center justify-center text-sm text-slate-500 dark:text-slate-400">
        Đang tải…
      </div>
    );
  }

  // Chưa hoàn thiện hồ sơ → chặn quẹt, hướng dẫn hoàn thiện.
  if (!isProfileComplete) {
    return (
      <div className="mt-8 flex min-h-[400px] flex-col items-center justify-center px-6 text-center">
        <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-pink-50 dark:bg-pink-900/20">
          <UserRoundCheck size={40} className="text-pink-500" />
        </div>
        <h3 className="mb-2 text-xl font-bold text-[#18191E] dark:text-white">
          Hồ sơ hẹn hò chưa hoàn thiện
        </h3>
        <p className="mb-6 max-w-[420px] text-sm text-slate-500 dark:text-slate-400">
          Hãy bổ sung <b>ảnh, tên, giới tính, tuổi</b> và{' '}
          <b>giới thiệu bản thân</b> để bắt đầu quẹt. Hồ sơ đầy đủ giúp người đối
          diện hiểu bạn hơn và dễ kết nối hơn.
        </p>
        <button
          type="button"
          onClick={() => changeTab?.('profile')}
          className="rounded-full bg-[#0057FF] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
        >
          Hoàn thiện hồ sơ ngay
        </button>
      </div>
    );
  }

  return (
    <div className="mt-8 text-sm font-medium dark:bg-dark2 p-4 select-none">
      <div className="flex justify-center md:min-h-[600px] min-h-[520px] relative">
        {/* Added relative positioning */}
        <div className="relative md:w-[350px] w-full h-96">
          {isOutOfPeople && (
            <div className="absolute inset-0 flex items-center justify-center">
              Vui lòng quay lại sau bạn nhé !
            </div>
          )}
          {_.map(people, (item, index) => {
            return (
              <TinderCard
                ref={childRefs[index]}
                key={index}
                className="absolute inset-0 " // Position absolute here
                preventSwipe={['up', 'down']}
                onSwipe={(dir) => swiped(dir, item.profileId, index)}
                onCardLeftScreen={() => outOfFrame(item.profileId, index)}
              >
                <div
                  style={{ zIndex: people.length - index }}
                  className="w-full shadow-lg rounded-lg cursor-pointer bg-white dark:bg-dark2 "
                >
                  <div
                    style={{
                      backgroundImage: `url(${
                        _.get(item, 'avatar') ||
                        '/default-avatar.svg'
                      })`,
                    }}
                    className="w-full h-72 rounded-lg bg-cover bg-center"
                  />
                  <div className="md:py-2 py-4 px-6 ">
                    <h2 className="text-xl font-semibold mb-2">
                      {item.nickname}
                    </h2>
                    <div className="flex flex-col gap-4">
                      <RenderInfoField
                        label="Giới thiệu"
                        value={item.description}
                        icon={<CircleUser size={20} />}
                      />
                      <div className="flex gap-2 justify-between">
                        <RenderInfoField
                          label="Tuổi"
                          value={item.age}
                          icon={<FaCalendarDay size={20} />}
                        />
                        <RenderInfoField
                          label="Giới tính"
                          value={item.gender}
                          icon={<MdOutlineNaturePeople size={20} />}
                        />
                      </div>
                      <RenderInfoField
                        label="Tỉnh/Thành phố"
                        value={item.city}
                        icon={<FaCity size={20} />}
                      />
                      <RenderInfoField
                        label="Sở thích"
                        value={item.interests.join(', ')}
                        icon={<MdOutlineInterests size={20} />}
                      />
                      <div className="flex gap-2 justify-between">
                        <RenderInfoField
                          label="Nghề nghiệp"
                          value={item.occupation}
                          icon={<FaBriefcase size={20} />}
                        />
                        <RenderInfoField
                          label="Học vấn"
                          value={item.education}
                          icon={<FaGraduationCap size={20} />}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="justify-between py-2 px-4 hidden md:flex">
                    <Button onClick={() => swipe('left')} direction="left" />
                    <ButtonViewProfile
                      profile={item}
                      onClick={() => openViewProfile(item)}
                    />
                    <Button onClick={() => swipe('right')} direction="right" />
                  </div>
                </div>
              </TinderCard>
            );
          })}
        </div>
      </div>
      <div className="flex justify-between mt-6 md:hidden">
        <Button onClick={() => swipe('left')} direction="left" />
        <ButtonViewProfile
          profile={people[currentIndex]}
          onClick={() => openViewProfile(people[currentIndex])}
        />
        <Button onClick={() => swipe('right')} direction="right" />
      </div>
      <DialogComponent
        size="full"
        visible={visible}
        onClose={() => setVisible(false)}
        title=""
        customSize="w-full max-w-[550px]"
        placement="CENTER"
      >
        <div className=" p-[14px] overflow-y-auto">
          {/* <Albums disableUpload profile={profile} /> */}
          <div className="flex gap-3">
            {profile?.albumUrls ? (
              profile?.albumUrls?.length > 1 ? (
                <div className="flex max-w-[231px] justify-center items-center">
                  <Slider
                    ref={sliderRef}
                    {...settings}
                    className="w-full max-w-[231px] h-full"
                  >
                    {profile?.albumUrls?.map((image, index) => (
                      <div
                        key={image}
                        className=" bg-black w-full !flex items-center"
                      >
                        <img
                          className="max-w-[231px] w-auto h-auto object-cover"
                          src={image}
                          alt={`Slide ${index + 1}`}
                        />
                      </div>
                    ))}
                  </Slider>
                </div>
              ) : (
                profile?.albumUrls?.map((image, index) => (
                  <div
                    key={image}
                    className="w-full rounded flex justify-center items-center bg-black"
                  >
                    <img
                      src={image}
                      alt={`Slide ${index + 1}`}
                      className="max-w-[231px] rounded object-cover"
                    />
                  </div>
                ))
              )
            ) : (
              <div className="max-w-[231px] rounded object-cover">
                <span>Chưa có hình ảnh được tải lên!</span>
              </div>
            )}
            <div className="flex flex-col w-1/2">
              <div className="flex justify-between items-center">
                <span className="text-[#111827] text-base font-semibold">
                  {profile?.nickname}
                </span>
                <svg
                  onClick={() => setVisible(false)}
                  className=" cursor-pointer"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="24" height="24" rx="12" fill="#EAEFFA" />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.8536 7.14645C17.0488 7.34171 17.0488 7.65829 16.8536 7.85355L7.85355 16.8536C7.65829 17.0488 7.34171 17.0488 7.14645 16.8536C6.95118 16.6583 6.95118 16.3417 7.14645 16.1464L16.1464 7.14645C16.3417 6.95118 16.6583 6.95118 16.8536 7.14645Z"
                    fill="#657384"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.14645 7.14645C7.34171 6.95118 7.65829 6.95118 7.85355 7.14645L16.8536 16.1464C17.0488 16.3417 17.0488 16.6583 16.8536 16.8536C16.6583 17.0488 16.3417 17.0488 16.1464 16.8536L7.14645 7.85355C6.95118 7.65829 6.95118 7.34171 7.14645 7.14645Z"
                    fill="#657384"
                  />
                </svg>
              </div>
              <div className="flex flex-col gap-1 mt-6">
                <div className="p-1 gap-1 flex items-center">
                  <svg
                    width="16"
                    height="17"
                    viewBox="0 0 16 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.7467 6.13341C13.0467 3.05341 10.36 1.66675 8 1.66675C8 1.66675 8 1.66675 7.99334 1.66675C5.64 1.66675 2.94667 3.04675 2.24667 6.12675C1.46667 9.56675 3.57334 12.4801 5.48 14.3134C6.18667 14.9934 7.09334 15.3334 8 15.3334C8.90667 15.3334 9.81334 14.9934 10.5133 14.3134C12.42 12.4801 14.5267 9.57341 13.7467 6.13341ZM8 9.47341C6.84 9.47341 5.9 8.53341 5.9 7.37341C5.9 6.21341 6.84 5.27341 8 5.27341C9.16 5.27341 10.1 6.21341 10.1 7.37341C10.1 8.53341 9.16 9.47341 8 9.47341Z"
                      fill="#657384"
                    />
                  </svg>
                  <span className="text-[#111827] truncate text-base">
                    {_.get(profile, 'additionLocation') || 'Chưa có'}
                  </span>
                </div>

                <div className="p-1 gap-1 flex items-center">
                  <svg
                    width="16"
                    height="17"
                    viewBox="0 0 16 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.7933 1.97341C14.7467 1.85341 14.6467 1.75341 14.5267 1.70675C14.46 1.68008 14.4 1.66675 14.3333 1.66675H9.99999C9.72666 1.66675 9.49999 1.89341 9.49999 2.16675C9.49999 2.44008 9.72666 2.66675 9.99999 2.66675H13.1267L10.12 5.68008H10.1133C9.21999 4.94008 8.07999 4.50008 6.83332 4.50008C3.97999 4.50008 1.66666 6.81341 1.66666 9.66675C1.66666 12.5201 3.97999 14.8334 6.83332 14.8334C9.68666 14.8334 12 12.5201 12 9.66675C12 8.42008 11.56 7.28008 10.82 6.38675L13.8333 3.37341V6.50008C13.8333 6.77341 14.06 7.00008 14.3333 7.00008C14.6067 7.00008 14.8333 6.77341 14.8333 6.50008V2.16675C14.8333 2.10008 14.82 2.04008 14.7933 1.97341Z"
                      fill="#657384"
                    />
                  </svg>
                  <span className="text-[#111827] truncate text-base">
                    {_.find(gender, { value: _.get(profile, 'gender') })
                      ?.label ||
                      _.get(profile, 'gender') ||
                      'Chưa có'}
                  </span>
                </div>
                <div className="p-1 gap-1 flex items-center">
                  <svg
                    width="16"
                    height="17"
                    viewBox="0 0 16 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.06 5.15326C13.4933 4.5266 12.5467 4.21327 11.1733 4.21327H11.0133V4.1866C11.0133 3.0666 11.0133 1.67993 8.50667 1.67993H7.49334C4.98667 1.67993 4.98667 3.07327 4.98667 4.1866V4.21993H4.82667C3.44667 4.21993 2.50667 4.53326 1.94 5.15993C1.28 5.89326 1.3 6.87993 1.36667 7.55326L1.37334 7.59993L1.41746 8.06321C1.43172 8.21299 1.51247 8.34843 1.63863 8.43041C1.79899 8.53461 2.02021 8.6756 2.16 8.75327C2.25334 8.81327 2.35334 8.8666 2.45334 8.91993C3.59334 9.5466 4.84667 9.9666 6.12 10.1733C6.18 10.7999 6.45334 11.5333 7.91334 11.5333C9.37334 11.5333 9.66 10.8066 9.70667 10.1599C11.0667 9.93993 12.38 9.4666 13.5667 8.77326C13.6067 8.75326 13.6333 8.73326 13.6667 8.71326C13.9104 8.57549 14.1626 8.40823 14.3964 8.24162C14.5099 8.16075 14.5821 8.03452 14.5975 7.89601L14.6 7.87327L14.6333 7.55993C14.64 7.51993 14.64 7.4866 14.6467 7.43993C14.7 6.7666 14.6867 5.8466 14.06 5.15326ZM8.72667 9.71993C8.72667 10.4266 8.72667 10.5333 7.90667 10.5333C7.08667 10.5333 7.08667 10.4066 7.08667 9.7266V8.8866H8.72667V9.71993ZM5.94 4.21327V4.1866C5.94 3.05327 5.94 2.63327 7.49334 2.63327H8.50667C10.06 2.63327 10.06 3.05993 10.06 4.1866V4.21993H5.94V4.21327Z"
                      fill="#657384"
                    />
                    <path
                      d="M13.6456 9.78343C14.0003 9.61751 14.4077 9.8984 14.3723 10.2884L14.16 12.6266C14.02 13.9599 13.4733 15.3199 10.54 15.3199H5.46C2.52666 15.3199 1.98 13.9599 1.84 12.6332L1.63929 10.4254C1.60424 10.0399 2.00217 9.75939 2.3558 9.91673C3.11553 10.2548 4.25602 10.7377 5.0297 10.9538C5.19329 10.9995 5.32583 11.118 5.40392 11.2688C5.82478 12.0816 6.68645 12.5132 7.91333 12.5132C9.12818 12.5132 9.99989 12.0649 10.4226 11.249C10.5008 11.0981 10.6338 10.9796 10.7974 10.9335C11.6223 10.7012 12.845 10.1579 13.6456 9.78343Z"
                      fill="#657384"
                    />
                  </svg>

                  <span className="text-[#111827] truncate text-base">
                    {_.get(profile, 'education') || 'Chưa có'}
                  </span>
                </div>
                <div className="p-1 gap-1 flex items-center">
                  <svg
                    width="16"
                    height="17"
                    viewBox="0 0 16 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.6667 5.16675C12.5733 5.16675 12.5 5.24008 12.5 5.33341V8.50008C12.5 8.77341 12.2733 9.00008 12 9.00008C11.7267 9.00008 11.5 8.77341 11.5 8.50008V5.33341C11.5 5.24008 11.4267 5.16675 11.3333 5.16675H10C9.90668 5.16675 9.83334 5.24008 9.83334 5.33341V6.90008C9.83334 7.17341 9.60668 7.40008 9.33334 7.40008C9.06001 7.40008 8.83334 7.17341 8.83334 6.90008V5.33341C8.83334 5.24008 8.76001 5.16675 8.66668 5.16675H7.36001C7.26668 5.16675 7.19334 5.24008 7.19334 5.33341L7.16668 8.50675C7.16668 8.78008 6.94001 9.00008 6.66668 9.00008C6.66668 9.00008 6.66668 9.00008 6.66001 9.00008C6.38668 9.00008 6.16668 8.77341 6.16668 8.49341L6.19334 5.33341C6.19334 5.24008 6.12001 5.16675 6.02668 5.16675H4.66668C4.57334 5.16675 4.50001 5.24008 4.50001 5.33341V7.70008C4.50001 7.97341 4.27334 8.20008 4.00001 8.20008C3.72668 8.20008 3.50001 7.97341 3.50001 7.70008V5.33341C3.50001 5.24008 3.42668 5.16675 3.33334 5.16675C2.22668 5.16675 1.33334 6.06008 1.33334 7.16675V9.83341C1.33334 10.9401 2.22668 11.8334 3.33334 11.8334H12.6667C13.7733 11.8334 14.6667 10.9401 14.6667 9.83341V7.16675C14.6667 6.06008 13.7733 5.16675 12.6667 5.16675Z"
                      fill="#657384"
                    />
                  </svg>
                  <span className="text-[#111827] truncate text-base">
                    {_.get(profile, 'height') || 'Chưa có'}
                  </span>
                </div>
                <div className="p-1 gap-1 flex items-center">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.96 2.06665C9.75334 2.06665 8.67334 2.65332 8.00001 3.55332C7.32668 2.65332 6.24668 2.06665 5.04001 2.06665C2.99334 2.06665 1.33334 3.73332 1.33334 5.79332C1.33334 6.58665 1.46001 7.31998 1.68001 7.99998C2.73334 11.3333 5.98001 13.3266 7.58668 13.8733C7.81334 13.9533 8.18668 13.9533 8.41334 13.8733C10.02 13.3266 13.2667 11.3333 14.32 7.99998C14.54 7.31998 14.6667 6.58665 14.6667 5.79332C14.6667 3.73332 13.0067 2.06665 10.96 2.06665Z"
                      fill="#657384"
                    />
                  </svg>

                  <span className="text-[#111827] truncate text-base">
                    {_.get(profile, 'genderPreference') || 'Chưa có'}
                  </span>
                </div>
              </div>
              <div className="flex flex-col py-1 gap-1">
                <span className="text-[#111827] text-base font-medium">
                  Mô tả
                </span>
                <p className="text-wrap text-md text-[#A1A5B6] ">
                  {profile?.description}
                </p>
              </div>
              <div
                onClick={() => {
                  setVisible2(true);
                  setVisible(false);
                }}
                className=" flex cursor-pointer py-3 items-center gap-1"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_621_132054)">
                    <path
                      d="M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z"
                      fill="url(#paint0_linear_621_132054)"
                    />
                    <g filter="url(#filter0_d_621_132054)">
                      <path
                        d="M17.8333 9.40423C17.8333 9.78923 17.5183 10.1042 17.1333 10.1042H6.86666C6.48166 10.1042 6.16666 9.78923 6.16666 9.40423V9.3984C6.16666 8.06256 7.24582 6.9834 8.58166 6.9834H15.4125C16.7483 6.9834 17.8333 8.0684 17.8333 9.40423Z"
                        fill="url(#paint1_linear_621_132054)"
                      />
                      <path
                        d="M6.16666 11.6792V14.6017C6.16666 15.9376 7.24582 17.0167 8.58166 17.0167H15.4125C16.7483 17.0167 17.8333 15.9317 17.8333 14.5959V11.6792C17.8333 11.2942 17.5183 10.9792 17.1333 10.9792H6.86666C6.48166 10.9792 6.16666 11.2942 6.16666 11.6792ZM9.66666 15.0626H8.49999C8.26082 15.0626 8.06249 14.8642 8.06249 14.6251C8.06249 14.3859 8.26082 14.1876 8.49999 14.1876H9.66666C9.90582 14.1876 10.1042 14.3859 10.1042 14.6251C10.1042 14.8642 9.90582 15.0626 9.66666 15.0626ZM13.4583 15.0626H11.125C10.8858 15.0626 10.6875 14.8642 10.6875 14.6251C10.6875 14.3859 10.8858 14.1876 11.125 14.1876H13.4583C13.6975 14.1876 13.8958 14.3859 13.8958 14.6251C13.8958 14.8642 13.6975 15.0626 13.4583 15.0626Z"
                        fill="url(#paint2_linear_621_132054)"
                      />
                    </g>
                  </g>
                  <defs>
                    <filter
                      id="filter0_d_621_132054"
                      x="-7"
                      y="-7"
                      width="38"
                      height="38"
                      filterUnits="userSpaceOnUse"
                      colorInterpolationFilters="sRGB"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset />
                      <feGaussianBlur stdDeviation="6" />
                      <feComposite in2="hardAlpha" operator="out" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0.322539 0 0 0 0 0.784844 0 0 0 0.5 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_621_132054"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_621_132054"
                        result="shape"
                      />
                    </filter>
                    <linearGradient
                      id="paint0_linear_621_132054"
                      x1="24.0536"
                      y1="24"
                      x2="0.0535705"
                      y2="-7.15256e-07"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#4191FF" />
                      <stop offset="1" stopColor="#015CE0" />
                    </linearGradient>
                    <linearGradient
                      id="paint1_linear_621_132054"
                      x1="6.22499"
                      y1="7.80006"
                      x2="17.8917"
                      y2="9.4334"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#64BEFF" />
                      <stop offset="1" stopColor="#E9F3FF" />
                    </linearGradient>
                    <linearGradient
                      id="paint2_linear_621_132054"
                      x1="6.92499"
                      y1="12.9334"
                      x2="15.7917"
                      y2="17.8334"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#64BEFF" />
                      <stop offset="1" stopColor="#E9F3FF" />
                    </linearGradient>
                    <clipPath id="clip0_621_132054">
                      <path
                        d="M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z"
                        fill="white"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <span className="text-md text-[#287CF1] font-medium">
                  Thông tin chi tiết
                </span>
              </div>
            </div>
          </div>
          {/* <Profile profile={profile} /> */}
        </div>
      </DialogComponent>
      <DialogComponent
        size="full"
        visible={visible2}
        onClose={() => {
          setVisible2(false);
          setVisible(true);
        }}
        title="Thông tin chi tiết"
        customSize="w-full max-w-[956px]"
        placement="CENTER"
      >
        <Profile profile={profile} />
      </DialogComponent>
    </div>
  );
};

// const Loading = () => {
//   return (
//     <>
//       <Skeleton className="h-72" />
//       <Skeleton height={50} />
//     </>
//   );
// };

const Button = ({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
}) => {
  return (
    <button
      className=" w-14 h-14 flex items-center justify-center  rounded-full"
      onClick={onClick}
    >
      {direction === 'right' ? <IconHeart /> : <IconCross />}
    </button>
  );
};

const ButtonViewProfile = ({
  profile,
  onClick,
}: {
  profile: IDatingProfileModel;
  onClick: () => void;
}) => {
  return (
    <button
      className=" w-14 h-14 flex items-center justify-center rounded-full bg-green-500"
      onClick={onClick}
    >
      <FaUserCircle size={70} className="text-white" />
    </button>
  );
};

const IconHeart = () => {
  return (
    <img src={heartIcon} alt="heart" className="w-full h-full rounded-full" />
  );
};

const IconCross = () => {
  return (
    <img src={crossIcon} alt="cross" className="w-full h-full rounded-full" />
  );
};

export default Connection;
